// src/services/studentsService.js
import apiClient from './api.js';

class StudentsService {
  // Get all students with filtering and pagination
  async getStudents(params = {}) {
    try {
      const response = await apiClient.get('/students', params);
      return response;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  }

  // Get single student by ID
  async getStudent(id) {
    try {
      const response = await apiClient.get(`/students/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching student:', error);
      throw error;
    }
  }

  // Create new student
  async createStudent(studentData) {
    try {
      const response = await apiClient.post('/students', studentData);
      return response;
    } catch (error) {
      console.error('Error creating student:', error);
      throw error;
    }
  }

  // Update existing student
  async updateStudent(id, studentData) {
    try {
      const response = await apiClient.put(`/students/${id}`, studentData);
      return response;
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  }

  // Delete student
  async deleteStudent(id) {
    try {
      const response = await apiClient.delete(`/students/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  }

  // Clone student
  async cloneStudent(id) {
    try {
      const response = await apiClient.post(`/students/${id}/clone`);
      return response;
    } catch (error) {
      console.error('Error cloning student:', error);
      throw error;
    }
  }

  // Get student enrollments
  async getStudentEnrollments(id) {
    try {
      const response = await apiClient.get(`/students/${id}/enrollments`);
      return response;
    } catch (error) {
      console.error('Error fetching student enrollments:', error);
      throw error;
    }
  }

  // Enroll student in class
  async enrollStudent(studentId, classId) {
    try {
      const response = await apiClient.post(`/students/${studentId}/enroll`, { classId });
      return response;
    } catch (error) {
      console.error('Error enrolling student:', error);
      throw error;
    }
  }

  // Unenroll student from class
  async unenrollStudent(studentId, classId) {
    try {
      const response = await apiClient.delete(`/students/${studentId}/enroll/${classId}`);
      return response;
    } catch (error) {
      console.error('Error unenrolling student:', error);
      throw error;
    }
  }

  /**
   * Get all students by parent email (for quick enrollment)
   * @param {string} email - Parent email address
   * @param {boolean} isPublic - Whether to use public endpoint (no auth required)
   * @returns {Promise<Array>} Array of student objects
   */
  async getStudentsByEmail(email, isPublic = false) {
    try {
      if (!email || !email.trim()) {
        return [];
      }

      // URL encode the email to handle special characters
      const encodedEmail = encodeURIComponent(email.trim());

      // Use public endpoint if specified (for public enrollment/waitlist)
      const endpoint = isPublic
        ? `/students/public/by-email/${encodedEmail}`
        : `/students/by-email/${encodedEmail}`;

      const response = await apiClient.get(endpoint);

      if (response.students && Array.isArray(response.students)) {
        return this.transformStudentsFromBackend(response.students);
      }

      return [];
    } catch (error) {
      console.error('Error fetching students by email:', error);
      // Don't throw - just return empty array for better UX
      return [];
    }
  }

  async getStudentsByPhone(phone, isPublic = false) {
    try {
      if (!phone || !phone.trim()) {
        return [];
      }

      // URL encode the phone to handle special characters
      const encodedPhone = encodeURIComponent(phone.trim());

      // Use public endpoint if specified (for public enrollment/waitlist)
      const endpoint = isPublic
        ? `/students/public/by-phone/${encodedPhone}`
        : `/students/by-phone/${encodedPhone}`;

      const response = await apiClient.get(endpoint);

      if (response.students && Array.isArray(response.students)) {
        return this.transformStudentsFromBackend(response.students);
      }

      return [];
    } catch (error) {
      console.error('Error fetching students by phone:', error);
      // Don't throw - just return empty array for better UX
      return [];
    }
  }

  /**
   * Transform student data from backend snake_case to frontend camelCase
   * @param {Array} students - Array of backend student objects
   * @returns {Array} Transformed student objects
   */
  transformStudentsFromBackend(students) {
    return students.map(student => this.transformStudentFromBackend(student));
  }

  /**
   * Transform single student from backend format
   * @param {Object} student - Backend student object
   * @returns {Object} Transformed student object
   */
  transformStudentFromBackend(student) {
    return {
      id: student.id,
      studentFirstName: student.student_first_name,
      studentLastName: student.student_last_name,
      studentAge: student.student_age,
      dateOfBirth: student.date_of_birth ? student.date_of_birth.split('T')[0] : '',
      gender: student.gender,
      parentName: student.parent_name,
      parentEmail: student.parent_email,
      parentPhone: student.parent_phone,
      emergencyContactName: student.emergency_contact_name,
      emergencyContactPhone: student.emergency_contact_phone,
      emergencyContactRelationship: student.emergency_contact_relationship,
      medicalNotes: student.medical_notes || '',
      age: student.student_age,
      enrollmentCount: student.enrollment_count || 0
    };
  }
}

// Create singleton instance
const studentsService = new StudentsService();

export default studentsService;